/**
 * BlogTeaser — "Latest from the blog" strip on /
 *
 * Pulls the three most-recently-published posts from /api/blog and renders
 * them as clean cards. Falls back to a silent null if the API fails or
 * returns zero posts, so a transient outage never leaves a sad empty box
 * on the homepage.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";

export default function BlogTeaser() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/blog", { params: { limit: 3 } });
                if (!cancelled) setPosts((data.posts || []).slice(0, 3));
            } catch (_e) {
                if (!cancelled) setPosts([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    if (loading) return null;
    if (!posts.length) return null;

    return (
        <section className="section-pad" data-testid="home-blog-teaser">
            <div className="container-page">
                <div className="flex items-end justify-between flex-wrap gap-3 mb-10">
                    <div>
                        <p className="overline">Fresh thinking</p>
                        <h2 className="mt-2 font-heading font-medium text-3xl sm:text-4xl tracking-tight text-foreground">
                            From the Simonara blog.
                        </h2>
                        <p className="mt-3 text-muted-foreground max-w-xl">
                            Short, practical reads for the people doing the hiring — and the people landing the role.
                        </p>
                    </div>
                    <Link
                        to="/resources#blog"
                        data-testid="home-blog-teaser-all"
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-green hover:underline"
                    >
                        Browse every article <ArrowRight size={14} />
                    </Link>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {posts.map((p, i) => (
                        <motion.div
                            key={p.id || p.slug}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{ duration: 0.4, delay: i * 0.05 }}
                            data-testid="home-blog-card"
                        >
                            <Link
                                to={`/blog/${p.slug}`}
                                data-testid={`home-blog-card-link-${p.slug}`}
                                className="card-soft p-6 flex flex-col h-full hover:border-emerald-500/40 hover:-translate-y-0.5 transition-all"
                            >
                                {p.cover_image_url ? (
                                    <img
                                        src={p.cover_image_url}
                                        alt=""
                                        aria-hidden="true"
                                        className="mb-4 rounded-lg h-40 w-full object-cover border border-border"
                                    />
                                ) : (
                                    <div className="mb-4 rounded-lg h-40 w-full bg-gradient-to-br from-brand-navy/90 to-emerald-700/70 border border-border" aria-hidden="true" />
                                )}
                                <Badge variant="outline" className="text-[11px] text-muted-foreground w-max">{p.category}</Badge>
                                <h3 className="mt-4 font-heading font-semibold text-lg text-foreground leading-snug">
                                    {p.title}
                                </h3>
                                <p className="mt-2 text-sm text-muted-foreground leading-relaxed line-clamp-3 flex-1">
                                    {p.excerpt}
                                </p>
                                <div className="mt-5 pt-4 border-t border-border flex items-center justify-between">
                                    <span className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                                        <Clock size={12} /> {p.reading_time || "5 min read"}
                                    </span>
                                    <span className="text-sm font-semibold text-brand-green dark:text-emerald-400">
                                        Read article →
                                    </span>
                                </div>
                            </Link>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
